import CorePortal from './CorePortal';
import React, { useRef } from 'react';
import styled, { css } from 'styled-components';

type ModalType = 'alert' | 'alert_full' | 'confirm' | 'common';

interface IModal {
	modal_type: ModalType;
	top?: number | null;
	left?: string | number | null;
	bottom?: number | null;
	right?: number | null;
	children?: JSX.Element | JSX.Element[];
	setModal?: (value: boolean) => void;
	bg_colored?: boolean;
	is_bg?: boolean;
	is_dnd?: boolean;
	x?: string;
	y?: string;
	radius?: number;
}

const CoreModal = ({
	modal_type,
	top,
	left,
	bottom,
	right,
	children,
	setModal = () => undefined,
	bg_colored,
	is_bg = true,
	is_dnd,
	x,
	y,
	radius,
}: IModal) => {
	const modal_ref = useRef<HTMLDivElement>(null);

	return (
		<CorePortal>
			<OverContainer>
				{is_bg ? (
					<ModalBgWrapper
						onClick={() => {
							setModal(false);
						}}
						bg_colored={bg_colored ?? modal_type !== 'common'}
					/>
				) : (
					<></>
				)}

				<ModalWrapper
					top={top}
					left={left}
					bottom={bottom}
					right={right}
					modal_type={modal_type}
					ref={modal_ref}
					is_dnd={is_dnd}
					x={x}
					y={y}
					radius={radius}
				>
					{children}
				</ModalWrapper>
			</OverContainer>
		</CorePortal>
	);
};

const ModalBgWrapper = styled.div<{ bg_colored: boolean }>`
	width: 100%;
	min-width: 1024px;
	height: 100vh;

	position: fixed;
	top: 0;
	left: 0;
	z-index: 9000;

	${({ bg_colored, theme }) =>
		bg_colored &&
		`
		background-color: ${theme.colors.gray.gray_black};
		opacity: 0.25;
	`}
`;

const ModalWrapper = styled.div<{
	top?: number | null;
	left?: string | number | null;
	bottom?: number | null;
	right?: number | null;
	x?: string;
	y?: string;
	modal_type: ModalType;
	is_dnd?: boolean;
	radius?: number;
}>`
	width: ${({ modal_type }) => (modal_type === 'common' ? 'auto' : '480px')};
	height: ${({ modal_type }) => (modal_type === 'alert_full' ? '760px' : ' max-content')};
	max-height: 760px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-radius: ${({ radius }) => (radius !== undefined ? radius + 'px' : '8px')};
	box-shadow: ${({ theme }) => theme.shadow.depth04};
	/* overflow: hidden; */

	${({ top }) => (top || top === 0) && top >= 0 && `top: ${top}px;`}

	${({ bottom }) => (bottom || bottom === 0) && bottom > 0 && `bottom: ${bottom}px`};
	${({ left }) =>
		typeof left === 'string'
			? `left: ${left}`
			: (left || left === 0) && left >= 0 && `left: ${left}px`};
	${({ right }) => (right || right === 0) && right > 0 && `bottom: ${right}px`};
	${({ x, y }) => {
		if (x && !y)
			return css`
				transform: translateX(${x});
			`;
		else if (!x && y)
			return css`
				transform: translateY(${y});
			`;
		else if (x && y)
			return css`
				transform: translate(${x}, ${y});
			`;
	}}
	z-index: 9999;
	position: absolute;

	${({ modal_type, is_dnd }) =>
		modal_type !== 'common' &&
		!is_dnd &&
		`
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	`};
	${({ modal_type, is_dnd }) =>
		modal_type !== 'common' &&
		is_dnd &&
		`
		top: calc(50% - 380px);
		left: calc(50% - 240px);
	`};
`;

const OverContainer = styled.div`
	width: 100vw;
	height: 100vh;
	overflow: hidden;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 8999;

	&::-webkit-scrollbar {
		display: none;
	}
`;

export default CoreModal;
